import { computed, ref, toRef } from 'vue';
import { useRoute } from 'vue-router';
import type { ArrayElementType } from '@/core/types/gengeric';
import {
  type AccommodationObject2Query,
  type AccommodationObjects2Query,
  type AddAccommodationObjectMutationVariables,
  ApproveStatus,
  ContractType,
  LegalOrigin,
  useAccommodationObject2Query,
  useAccommodationObjects2Query,
  useAccomodationObjectDashboardInfoQuery,
  useAddAccommodationObjectMutation,
  useAddContractMutation,
} from '@/graphql/generated';

export type ObjectListItemType = ArrayElementType<
  AccommodationObjects2Query['accommodationObjects2']['accommodationObjects']
>;

const accommodationObjects = ref<AccommodationObjects2Query['accommodationObjects2']>();
const accommodationObject = ref<AccommodationObject2Query['accommodationObject2']>();

export const useAccommodationObjectsStore = () => {
  const $route = useRoute();

  const _activeAccountNumber = toRef(() => +($route.params.acc_id || 0));

  const getAccounts = async () => {
    const { request } = useAccommodationObjects2Query({});
    const result = await request();

    if (result.data) {
      accommodationObjects.value = result.data.accommodationObjects2;
    }
  };

  const isUnknownStatus = toRef(() => accommodationObject.value?.commonInformation.status === ApproveStatus.OnApproval);

  const isActiveStatus = toRef(() => accommodationObject.value?.commonInformation.status === ApproveStatus.Approved);

  const isDeclinedStatus = toRef(() => accommodationObject.value?.commonInformation.status === ApproveStatus.Declined);

  const timeZoneOffsetMin = computed(() => accommodationObject.value?.timeZoneOffsetMin || -180);

  const loadObject = async (id: string) => {
    const { request } = useAccommodationObject2Query({ accommodationObjectCid: id });
    const result = await request();

    return result;
  };

  const isActiveObjectLoading = ref(false);

  const getActiveObject = async () => {
    const id = $route.params.id as string | undefined;
    if (!id) {
      return;
    }

    isActiveObjectLoading.value = true;
    accommodationObject.value = undefined;

    const result = await loadObject(id);

    if (!result.errors) {
      accommodationObject.value = result.data?.accommodationObject2;
    }

    isActiveObjectLoading.value = false;
  };

  const activeAccount = toRef(() => {
    if (_activeAccountNumber.value) {
      const account = accommodationObject.value?.contracts.find(
        (contract) => contract.contractNumber === _activeAccountNumber.value,
      );

      if (account) {
        return account;
      }
    }

    return accommodationObject.value?.contracts[0];
  });

  const getAccomodationObjectDashboardAnalytics = async () => {
    const id = $route.params.id as string | undefined;
    if (!id) {
      return;
    }

    const { request } = useAccomodationObjectDashboardInfoQuery({ accommodationObjectCid: id });
    const result = await request();

    return result;
  };

  const createAccount = async (payload: {
    legalCid: string;
    currencyIsoCode: string;
    legalOrigin: LegalOrigin;
    contractType: ContractType;
  }) => {
    if (!accommodationObject.value) {
      return;
    }

    const { request } = useAddContractMutation({
      ...payload,
      accommodationObjectCID: accommodationObject.value?.cid,
    });

    const result = await request();

    if (result.data && accommodationObject.value) {
      accommodationObject.value.contracts.push(result.data.addContract.Contract!);
    }

    return result;
  };

  const createObject = async (payload: AddAccommodationObjectMutationVariables) => {
    const { request } = useAddAccommodationObjectMutation(payload);
    const result = await request();

    return result;
  };

  return {
    accommodationObjects,
    accommodationObject,
    isUnknownStatus,
    isActiveStatus,
    isDeclinedStatus,
    timeZoneOffsetMin,
    activeAccount,

    createObject,
    getAccounts,
    loadObject,
    getActiveObject,
    createAccount,
    getAccomodationObjectDashboardAnalytics,
  };
};
